import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

/**
 * Lets a page ask the app shell to collapse its navigation, and give it back afterwards.
 *
 * <p><b>Why a service rather than the page toggling the sidebar itself.</b> The sidebar belongs to
 * the auth layout and its collapsed/expanded state is a <em>user preference</em>, persisted in
 * <code>localStorage</code>. A page that called <code>toggleSidebarCollapse()</code> directly would
 * overwrite that preference — someone who likes an expanded nav would find it collapsed for the
 * rest of their session because they once opened the AI assistant.</p>
 *
 * <p>So this expresses a temporary <em>request</em>, and the layout remembers what the person
 * actually chose and restores it on release. Reference-counted, because both AI surfaces can ask at
 * once: navigating to the AI page with the rail already open must not restore the nav when only one
 * of them lets go.</p>
 *
 * <p>The design calls for this in both shells — AI First is 81px nav + a 976px column, and opening
 * the Assist rail moves the shell from 280 + 1160 to 81 + 900 + 459. Measured before this existed,
 * the nav sat at 252px in every state.</p>
 */
@Injectable({ providedIn: 'root' })
export class ShellChromeService {
  private holders = new Set<string>();
  private readonly _navCollapsed = new BehaviorSubject<boolean>(false);

  /** True while any surface is asking for a collapsed nav. */
  readonly navCollapsed$ = this._navCollapsed.asObservable();

  /**
   * @param holder identifies the caller, so two surfaces asking at once are counted once each and
   *   a repeated request from the same one does not double-count.
   */
  requestCollapsedNav(holder: string): void {
    this.holders.add(holder);
    this.emit();
  }

  releaseCollapsedNav(holder: string): void {
    this.holders.delete(holder);
    this.emit();
  }

  private emit(): void {
    const collapsed = this.holders.size > 0;
    if (collapsed !== this._navCollapsed.value) {
      this._navCollapsed.next(collapsed);
    }
  }
}
